import { useMemo, useState } from 'react';
import useSWR from 'swr';

import api from '@api';
import apiHooks, {
    ICreateUseRequestInfiniteResponse,
    ICreateUseRequestResponse,
    useBalance,
} from '@apiHooks';
import {
    IGetManagerDataFormatted,
    IGetNotificationsDataFormatted,
    IGetTaxDataFormatted,
    IGetUserProfileDataFormatted,
} from '@api/types';
import useI18n from '@i18n';
import useLocalStorage from '@hooks/useLocalStorage';
import normalizeTaxData from '@utils/normalizeTaxData';

import { currencies, ICurrency } from '../../layout/Header/components/CurrencySwitch';
import { HeaderData } from '../../layout/Header/interfaces';

const { useManager, useNotifications, useTaxData, useUserProfile } = apiHooks;

const NOTIFICATIONS_LIMIT = 10;

interface IUnreadSupportMessagesCountResponse {
    attributes?: {
        unread_count?: number;
    };
}

const mergeUniqueIds = (prevIds: number[], newIds: number[]): number[] => {
    if (!newIds.length) return prevIds;

    const knownIds = new Set(prevIds);
    const idsToAppend = newIds.filter(id => !knownIds.has(id));

    return idsToAppend.length ? [...prevIds, ...idsToAppend] : prevIds;
};

const useHeaderData = (): HeaderData => {
    const { locale } = useI18n();
    const [sentNotificationIds, setSentNotificationIds] = useState<number[]>([]);
    const [currentCurrencyRaw, setCurrentCurrencyRaw] = useLocalStorage('currency', currencies[0]);
    const currentCurrency = (currentCurrencyRaw as ICurrency) || currencies[0];
    const setCurrentCurrency = setCurrentCurrencyRaw as (currency: ICurrency) => void;

    const { data: userProfile = null } = useUserProfile<IGetUserProfileDataFormatted>();
    const { data: balanceData } = useBalance();
    const { data: manager = null } = useManager<IGetManagerDataFormatted>({
        dependencies: [locale],
    }) as ICreateUseRequestResponse<IGetManagerDataFormatted>;

    const { data: rawTaxData = null } = useTaxData({
        condition: Boolean(userProfile?.hasTaxData),
        dependencies: [userProfile?.hasTaxData],
    }) as ICreateUseRequestInfiniteResponse<IGetTaxDataFormatted[]>;

    const {
        data: notifications = [],
        isLoading: isNotificationsLoading,
        nextPage,
        isReachingEnd,
        mutate: refreshNotifications,
        error: notificationsError,
    } = useNotifications({
        params: { limit: NOTIFICATIONS_LIMIT },
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetNotificationsDataFormatted[]>;

    const { data: supportUnreadCount = 0 } = useSWR(['unread-support-messages-count'], async () => {
        const {
            data,
        } = await api.support.getUnreadedMessagesCount<IUnreadSupportMessagesCountResponse>({});
        const { unread_count: unreadCount = 0 } = data?.attributes || {};
        return unreadCount;
    });

    const taxData = useMemo(() => normalizeTaxData(rawTaxData), [rawTaxData]);

    const { confirmed, hold } = useMemo(
        () =>
            balanceData?.balances.find(el => el.currency === currentCurrency.name) || {
                confirmed: 0,
                hold: 0,
            },
        [balanceData, currentCurrency],
    );

    const markRead = async (ids: number[]) => {
        const notSentIds = ids.filter(id => !sentNotificationIds.includes(id));

        if (!notSentIds.length) return;

        const requests = notSentIds.map((id, index) => ({
            type: 'request',
            id: index,
            attributes: {
                url: '/user/notifications/read',
                method: 'POST',
                body: { notification_id: id },
            },
        }));

        try {
            await api.batch.batch({ body: { data: requests } });
            refreshNotifications();
            setSentNotificationIds(prevIds => mergeUniqueIds(prevIds, notSentIds));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        }
    };

    return {
        userProfile,
        taxData,
        manager,
        profile: {
            profileImage: userProfile?.profileImage || '',
            currencyIcon: currentCurrency.icon,
            name: userProfile?.name?.slice(0, 10) || '',
            balance: confirmed,
            hold,
        },
        currentCurrency,
        setCurrentCurrency,
        supportUnreadCount,
        notifications: {
            items: notifications,
            isLoading: isNotificationsLoading,
            error: notificationsError,
            refresh: () => refreshNotifications(),
            loadNextPage: () => {
                if (!isReachingEnd) nextPage();
            },
            markRead,
        },
    };
};

export default useHeaderData;
